import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SignatureFieldHandle } from '@roc/ui';
import {
  ExtensionAgreementContent,
  ExtensionAgreementSignedMessage,
  ExtensionAgreementUnavailableMessage,
  SigningInfo,
} from '@roc/feature-documents';
import { ExtensionAgreementSigningService } from '../../services/extensionAgreementSigningService';

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: '100vh',
    width: '100%',
    padding: theme.spacing(4, 2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.grey[100],
  },
  container: {
    maxWidth: 900,
    width: '100%',
    padding: theme.spacing(6),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
  },
  statusCard: {
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(6),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
}));

export const ExtensionAgreementSigning = observer(() => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const service = useRef(new ExtensionAgreementSigningService()).current;
  const signatureRef = useRef<SignatureFieldHandle>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<SigningInfo | null>(null);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    loadInfo();
  }, [token]);

  const resolveErrorMessage = (e: any, fallback: string) =>
    e?.error?.response?.data?.error?.message ||
    e?.response?.data?.error?.message ||
    fallback;

  const loadInfo = async () => {
    setLoading(true);
    try {
      const response = await service.getExtensionSigningInfo(token);
      const body = response?.data;
      const data: SigningInfo =
        body && body.data && body.loanId === undefined ? body.data : body;
      setInfo(data || {});
      setCompleted(Boolean(data?.alreadySigned));
      setBlockedMessage(
        !data?.alreadySigned && data?.canSign === false
          ? data?.unavailableReason || ''
          : null
      );
    } catch (e) {
      setError(resolveErrorMessage(e, 'This signing link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!signatureRef.current?.isSigned()) {
      setError('Please sign the document before submitting it.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await service.generateExtensionAgreementDocument({
        token,
        signature: signatureRef.current.getSignature(),
        signerName: info?.borrowerName,
        userAgent: navigator.userAgent,
      });
      setCompleted(true);
    } catch (e) {
      setError(
        resolveErrorMessage(e, 'There was an error submitting your signature. Please try again.')
      );

      const status = e?.error?.response?.status ?? e?.response?.status;
      if (status === 400) {
        await loadInfo();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.statusCard}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !info) {
    return (
      <Box className={classes.page}>
        <Box className={classes.statusCard}>
          <Typography variant="h6">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (completed) {
    return (
      <Box className={classes.page}>
        <Box className={classes.statusCard}>
          <ExtensionAgreementSignedMessage borrowerName={info?.borrowerName} />
        </Box>
      </Box>
    );
  }

  if (blockedMessage !== null) {
    return (
      <Box className={classes.page}>
        <Box className={classes.statusCard}>
          <ExtensionAgreementUnavailableMessage message={blockedMessage} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.container}>
        <ExtensionAgreementContent
          info={info}
          signatureRef={signatureRef}
          onSubmit={handleSubmit}
          submitting={submitting}
          error={error}
        />
      </Box>
    </Box>
  );
});

export default ExtensionAgreementSigning;
